import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ICourseCategory } from "./ICourseCategory";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import { addCourseCategory } from "../../../Redux/Slices/CourseCategory/AddCourseCategorySlice";
import Select from "../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../Components/Select/ISelectProps";

interface CourseCategoriesFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: ICourseCategory;
  setInitialValues: React.Dispatch<React.SetStateAction<ICourseCategory>>;
}

const userSchema = z.object({
  id: z.number().optional(),
  name: z.string({
    required_error: "Please enter category name",
    invalid_type_error: "Please enter category name",
  }),
  course_category_code: z.string().optional(),
  course_category_id: z.string().optional(),
  description: z.string({
    required_error: "Please enter category description",
    invalid_type_error: "Please enter category description",
  }),
  status: z.boolean({
    message: "Please select the status",
  }),
});

const CourseCategoriesForm: FunctionComponent<CourseCategoriesFormProps> = (props) => {
  const dispatch = useAppDispatch();

  const methods = useForm<ICourseCategory>({
    defaultValues: {
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ICourseCategory> = (val) => {
    dispatch(addCourseCategory(val));
  };

  const QuestionTypeSatus = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_content"
      >
        <TextInputFieldC name="name" label="Category Name" type="text" />
        <TextInputFieldC name="description" label="Description" type="text" />
        
        <Select
          name="status"
          label="Select the status"
          options={QuestionTypeSatus ?? []}
          value={
            QuestionTypeSatus?.find(
              (list) => list.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value as boolean, {
              shouldValidate: true,
            });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(CourseCategoriesForm);
